import { translateBackend } from '@assets/i18n/config';
import { FeatureFlagTypes } from '@interfaces/organizationFeatureFlag';
import type { User } from '@interfaces/user';
import { Box } from '@ovComponents/1-primative/box/box';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { SelectionTile } from '@ovComponents/3-pattern/selectionTile/selectionTile';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { InlineImage } from '@pages/client/components/documents/inlineImage';
import { InlineImageLocal } from '@pages/client/components/documents/inlineImageLocal';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { UserContext } from '@providers/userContextProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { type FieldOptions, type FormErrors, validateFields } from '../utils';

export const RiskQuestionVisual = ({
  options,
  userData,
  loading,
  question,
  workflowCompletion,
  updateUser,
  continueFunc,
}: {
  options: any;
  userData: Partial<User>;
  loading: boolean;
  question: number;
  workflowCompletion?: any;
  updateUser: (user: Partial<User>) => void;
  continueFunc: () => void;
}) => {
  const { t } = useTranslation('client');
  const { sendAnalytic } = useContext(AnalyticsContext);
  const [focused, setFocused] = useState<string[]>([]);
  const [errors, setErrors] = useState<FormErrors>(null);
  const fieldOptions: FieldOptions = useMemo(
    () => ({
      riskQuestion1: { required: question === 1 },
      riskQuestion2: { required: question === 2 },
    }),
    [question],
  );

  const validate = useCallback(
    (candidateFields?: string[]): FormErrors => {
      const data = {
        riskQuestion1: userData.riskQuestion1,
        riskQuestion2: userData.riskQuestion2,
      };
      const newErrors = validateFields(fieldOptions, data, candidateFields);
      setErrors(newErrors);
      return newErrors;
    },
    [fieldOptions, userData],
  );

  const submit = async () => {
    const formErrors = validate();

    if (formErrors) {
      setFocused(Object.keys(formErrors));
      return;
    }

    if (!formErrors) {
      const analyticEvent =
        question === 1
          ? ovAnalyticsEvents.workflowsRiskQuestion1ContinueButtonSelect
          : ovAnalyticsEvents.workflowsRiskQuestion2ContinueButtonSelect;
      sendAnalytic(analyticEvent, {
        workflowStepTitle: options?.title,
        workflowId: workflowCompletion?.workflow?.id,
        workflowName: workflowCompletion?.workflow?.name,
        activeWorkflowCompletionId: workflowCompletion?.id,
        objectId: workflowCompletion?.objectId,
        objectType: workflowCompletion?.objectType,
      });
      await continueFunc();
    }
  };

  useEffect(() => {
    validate(focused);
  }, [validate, focused]);

  const questionField = question === 1 ? 'riskQuestion1' : 'riskQuestion2';

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      submitButtonProps={{
        label: t('continue'),
        disabled: loading,
        dataTestId: `risk-question-${question}-continue-button`,
      }}
    >
      {options?.title && (
        <Typography variant='displayLarge' sx={{ mt: 1, mb: 3 }}>
          {translateBackend(options?.title)}
          <Box display='inline-flex'>
            {additionalInfo(options?.title?.additionalInfo) && (
              <InfoDialog
                information={
                  additionalInfo(options?.title?.additionalInfo) ?? ''
                }
              />
            )}
          </Box>
        </Typography>
      )}
      {options?.subtitle && (
        <Typography
          display='flex'
          alignItems='end'
          variant='bodyLarge'
          component={'div'}
          sx={{ mb: 3, table: { width: '100%' } }}
        >
          <Box
            display='flex'
            gap={2}
            flexDirection='column'
            sx={{ m: 0, '& > *': { m: 0 } }}
          >
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackend(options?.subtitle)}
            </ReactMarkdown>
          </Box>
          {additionalInfo(options?.subtitle?.additionalInfo) && (
            <InfoDialog
              information={
                additionalInfo(options?.subtitle?.additionalInfo) ?? ''
              }
            />
          )}
        </Typography>
      )}

      {options?.attachment?.file && (
        <Box sx={{ mb: '20px' }}>
          <InlineImageLocal
            data-testid='attachment'
            file={options?.attachment?.file}
            style={{
              objectFit: 'contain',
              height: '300px',
              borderRadius: '15px',
            }}
          />
        </Box>
      )}
      {options?.attachment?.value && (
        <Box sx={{ mb: '20px' }}>
          <InlineImage
            data-testid='attachment'
            fileDocumentId={options?.attachment?.value}
            style={{
              objectFit: 'contain',
              height: '300px',
              borderRadius: '15px',
            }}
          />
        </Box>
      )}

      <Box>
        <SelectionTile
          testId={`risk-question-${question}`}
          onChange={(e: any) =>
            updateUser({ ...userData, [questionField]: e.target.value })
          }
          value={userData[questionField] ?? ''}
          options={options?.options?.map((option: any) => ({
            value: option.value,
            label: translateBackend(option.label),
            subtitle: translateBackend(option.description),
          }))}
          error={errors?.riskQuestion1?.active || errors?.riskQuestion2?.active}
        />
      </Box>
    </WorkflowCompletionForm>
  );
};

export default RiskQuestionVisual;
